import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatBRL } from "@/context/CartContext";
import { useAppConfig } from "@/context/AppConfigContext";
import { ShoppingBag, PackageX, Heart } from "lucide-react";
import ShopComingSoon from "@/components/ShopComingSoon";

function ProductCard({ p }) {
  const img = (p.images || [])[0];
  const soldOut = p.sold_out;
  return (
    <Link
      to={`/produtos/${p.slug}`}
      data-testid={`product-card-${p.slug}`}
      className="group relative rounded-3xl overflow-hidden border border-[hsl(var(--border))] bg-white hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
    >
      <div className="relative aspect-square bg-[hsl(var(--muted))] overflow-hidden">
        {img ? (
          <img
            src={img}
            alt={p.name}
            className={`w-full h-full ${p.category === "Kits" ? "object-contain bg-white" : "object-cover"} group-hover:scale-105 transition-transform duration-500`}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-[hsl(var(--muted-foreground))]">
            <ShoppingBag className="h-12 w-12" />
          </div>
        )}
        {soldOut && (
          <div className="absolute top-3 left-3 rounded-full bg-black/85 backdrop-blur text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 flex items-center gap-1.5" data-testid={`product-sold-out-${p.slug}`}>
            <PackageX className="h-3.5 w-3.5" /> Esgotado
          </div>
        )}
        <div className="absolute top-3 right-3 rounded-full bg-white/90 text-[hsl(var(--primary))] text-xs font-semibold px-2.5 py-1">
          {p.category}
        </div>
      </div>
      <div className="p-5">
        <h3 className="font-heading text-lg font-extrabold line-clamp-2 min-h-[3.5rem]">
          {p.name}
        </h3>
        <div className="mt-2 flex items-end justify-between gap-3">
          <div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">a partir de</div>
            <div className="font-heading text-2xl font-extrabold text-[hsl(var(--primary))]">
              {formatBRL(p.price_cents)}
            </div>
          </div>
          <span className={`text-xs font-semibold ${soldOut ? "text-[hsl(var(--muted-foreground))]" : "text-[hsl(var(--primary))]"}`}>
            Ver produto →
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function Products() {
  const { shopEnabled, loading: cfgLoading } = useAppConfig();
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!shopEnabled) return;
    api.get("/shop/products").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [shopEnabled]);

  if (cfgLoading) return null;
  if (!shopEnabled) return <ShopComingSoon />;

  return (
    <div className="container-cuidar py-12 sm:py-16">
      <div className="max-w-3xl">
        <div className="text-xs font-semibold tracking-widest uppercase text-[hsl(var(--accent))]">Loja solidária</div>
        <h1 className="mt-2 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">
          Produtos oficiais do <span className="text-[hsl(var(--primary))]">Instituto Cuidar</span>
        </h1>
        <p className="mt-4 text-[hsl(var(--muted-foreground))] max-w-2xl">
          Cada peça vestida é um sim ao acolhimento de famílias de crianças neuroatípicas.
          100% da renda mantém nossos programas de escuta, orientação e apoio jurídico.
        </p>
        <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] text-sm font-semibold px-4 py-1.5">
          <Heart className="h-4 w-4" /> 100% da arrecadação vai para o instituto
        </div>
      </div>

      <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="products-grid">
        {items === null && (
          <>
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-3xl aspect-[3/4] bg-[hsl(var(--muted))] animate-pulse" />
            ))}
          </>
        )}
        {Array.isArray(items) && items.length === 0 && (
          <div className="col-span-full surface p-12 text-center text-[hsl(var(--muted-foreground))]">
            Nenhum produto disponível no momento. Volte em breve!
          </div>
        )}
        {Array.isArray(items) && items.map((p) => <ProductCard key={p.id} p={p} />)}
      </div>
    </div>
  );
}
